import { formatRelative, type StatusLevel } from '@westhetics/ui'
import type { Delivery, Integration, IntegrationsCatalog, Scope, Webhook } from './api'

/**
 * Rótulos da área de Integrações (docs/areas/integracoes.md). O valor cru (`active`, `patients:write`, `failing`)
 * nunca aparece na tela; o vocabulário dos escopos e eventos vem do catálogo da API.
 */

/** As portas públicas da Westhetics, para os guias da tela (T30, T53). */
export const MCP_URL = 'https://mcp.westhetics.io'
export const API_URL = 'https://api.westhetics.io/v1'
export const DOCS_URL = 'https://docs.westhetics.io'
export const DOCS_MCP_URL = `${DOCS_URL}/mcp/visao-geral`
export const DOCS_WEBHOOKS_URL = `${DOCS_URL}/webhooks/visao-geral`

/** Âncoras dos cartões destrutivos do app, para chegar direto pela lista. */
export const REGENERATE_ANCHOR = 'regenerate-key'
export const REVOKE_ANCHOR = 'revoke'

export interface StatusLabel {
  label: string
  level: StatusLevel
}

export function integrationStatus(integration: Pick<Integration, 'status'>): StatusLabel {
  return integration.status === 'active'
    ? { label: 'Ativo', level: 'done' }
    : { label: 'Revogado', level: 'waiting' }
}

/** "último uso há 3 dias" · "ainda não usado". */
export function lastUsedText(lastUsedAt: string | null, now = new Date()): string {
  return lastUsedAt ? `último uso ${formatRelative(new Date(lastUsedAt), now)}` : 'ainda não usado'
}

/** "chave wx_1c0a58f5…": o que a tela mostra da chave; a inteira só aparece uma vez. */
export function keyHint(keyPrefix: string): string {
  return `chave wx_${keyPrefix}…`
}

export type Access = 'none' | 'read' | 'write'

/** O nível de um recurso nos escopos: escrever inclui ler (RN-INT-03). */
export function accessOf(
  scopes: readonly Scope[],
  resource: { read: Scope; write: Scope | null },
): Access {
  if (resource.write && scopes.includes(resource.write)) return 'write'
  return scopes.includes(resource.read) ? 'read' : 'none'
}

export const ACCESS_LABELS: Record<Access, string> = {
  none: 'Não acessa',
  read: 'Ler',
  write: 'Ler e escrever',
}

/** As permissões em uma linha: "Pacientes · ler e escrever · Documentos e assinaturas · ler". */
export function scopeSummary(
  scopes: readonly Scope[],
  catalog: IntegrationsCatalog | undefined,
): string {
  if (!catalog) return `${scopes.length} ${scopes.length === 1 ? 'permissão' : 'permissões'}`
  const parts = catalog.resources.flatMap((resource) => {
    const access = accessOf(scopes, resource)
    return access === 'none'
      ? []
      : [`${resource.label} · ${ACCESS_LABELS[access].toLocaleLowerCase('pt-BR')}`]
  })
  if (scopes.includes('anamneses:answers')) parts.push('respostas da anamnese')
  if (scopes.includes('webhooks:write')) parts.push('webhooks pela API')
  return parts.join(' · ')
}

/** O status do webhook: falhando é atenção, porque os eventos não estão chegando ao sistema da clínica. */
export function webhookStatus(
  webhook: Pick<Webhook, 'status' | 'disabledReason' | 'failingSince'>,
): StatusLabel {
  if (webhook.status === 'disabled') {
    return webhook.disabledReason === 'failing'
      ? { label: 'Desligado por falha', level: 'attention' }
      : { label: 'Desativado', level: 'waiting' }
  }
  return webhook.failingSince
    ? { label: 'Falhando', level: 'attention' }
    : { label: 'Ativo', level: 'done' }
}

export function deliveryStatus(delivery: Pick<Delivery, 'status' | 'attempts'>): StatusLabel {
  if (delivery.status === 'succeeded') return { label: 'Entregue', level: 'done' }
  if (delivery.status === 'failed') return { label: 'Falhou', level: 'attention' }
  return delivery.attempts > 0
    ? { label: 'Tentando de novo', level: 'waiting' }
    : { label: 'Na fila', level: 'waiting' }
}

/** "Todos os eventos" · "3 eventos". */
export function eventsSummary(events: readonly string[]): string {
  if (events.includes('*')) return 'Todos os eventos'
  return `${events.length} ${events.length === 1 ? 'evento' : 'eventos'}`
}

export const CHANNEL_LABELS: Record<'api' | 'mcp', string> = { api: 'API', mcp: 'MCP' }

/** O que a entrega respondeu, em texto: "HTTP 500 em 812 ms" · "sem resposta: tempo esgotado". */
export function responseText(
  delivery: Pick<Delivery, 'responseStatus' | 'durationMs' | 'error'>,
): string {
  if (delivery.responseStatus) {
    const time = delivery.durationMs !== null ? ` em ${delivery.durationMs} ms` : ''
    return `HTTP ${delivery.responseStatus}${time}`
  }
  if (delivery.error) return `sem resposta: ${delivery.error}`
  return 'ainda não tentou'
}

/** O domínio do endereço, para a lista (o caminho pode ter token). */
export function urlHost(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

/** "em 5 minutos" · "em 2 horas" · "no próximo minuto": quando a entrega tenta de novo. */
export function nextAttemptText(iso: string, now = new Date()): string {
  const minutes = Math.ceil((new Date(iso).getTime() - now.getTime()) / 60_000)
  if (minutes <= 1) return 'no próximo minuto'
  if (minutes < 60) return `em ${minutes} minutos`
  const hours = Math.round(minutes / 60)
  return `em ${hours} ${hours === 1 ? 'hora' : 'horas'}`
}

/** O `curl` de teste da chave: o primeiro passo de quem integra ("Comece por aqui"). */
export function curlTest(key: string) {
  return `curl ${API_URL}/me \\\n  -H "Authorization: Bearer ${key}"`
}
